import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "./config/constants";
import AminiiDashboard from "./js/AminiiDashboard";
import AminiiProductList from "./js/AminiiProductList";
import AminiiProductDetails from "./js/AminiiProductDetails";
import AdminAminii from "./Admin Controls/AdminAminii";
import Aminiilogin from "./js/AminiiLogin";
import Aminiiregister from "./js/AminiiRegister";
import AddToCart from "./js/AddToCart";
import AminiiCheckout from "./js/AminiiCheckout";
import AminiiOrderPage from "./js/AminiiOrderPage";
import ProtectedRoute from "./js/ProtectedRoute"; // Updated import path
import ForgotPasswordPage from "./js/ForgotPasswordPage";
import AminiiAbout from "./js/AminiiAbout";
import AminiiPolicies from "./js/AminiiPolicies";
import AminiiContact from "./js/AminiiContact";
import AminiiErrorPage from "./js/AminiiErrorPage";
import AminiiTrackOrder from "./js/AminiiTrackOrder";
import { AuthProvider } from "./api/AuthContext";
import AdminFloatingButton from "./components/AdminFloatingButton";
import MaintenanceUnderway from "./js/MaintenanceUnderway";
import { isMaintenanceModeActive } from "./config/maintenanceConfig";
import AdminBulkUpload from "./Admin Controls/AdminBulkUpload";
import AdminImageLinker from "./Admin Controls/AdminImageLinker";
import CartDrawer from "./components/CartDrawer";

/** Returns a persistent visitor ID stored in localStorage.
 *  Created once per browser — survives page navigations and refreshes.
 *  This is the key that the backend uses to deduplicate unique visitors
 *  instead of the IP address (which rotates on Render's free tier). */
function getOrCreateVisitorId() {
  try {
    const KEY = "aminii_vid";
    let vid = localStorage.getItem(KEY);
    if (!vid) {
      // Generate a random 24-char ID: timestamp + random
      vid =
        "v" +
        Date.now().toString(36) +
        Math.random().toString(36).substr(2, 12);
      localStorage.setItem(KEY, vid);
    }
    return vid;
  } catch (e) {
    // Private browsing / storage blocked — fall back to session-only ID
    return "session_" + Math.random().toString(36).substr(2, 10);
  }
}

function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    // Avoid tracking admin control routes
    if (location.pathname.toLowerCase().startsWith("/admin")) return;

    const isMobile =
      typeof window !== "undefined" &&
      (window.innerWidth <= 768 ||
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

    const visitorId = getOrCreateVisitorId();

    try {
      axios
        .post(
          `${API_BASE_URL}/track/visit`,
          {
            device: isMobile ? "mobile" : "desktop",
            page: location.pathname,
            visitorId: visitorId,
          },
          { timeout: 4000 }
        )
        .catch(() => {});
    } catch (e) {
      // Non-blocking
    }
  }, [location.pathname]);

  return null;
}

function App() {
  const isMaintenance = isMaintenanceModeActive();

  if (isMaintenance) {
    return (
      <BrowserRouter>
        <Routes>
          {/* Admin routes accessible during maintenance */}
          <Route
            path="/admin/bulk-upload"
            element={
              <AuthProvider>
                <AdminBulkUpload />
              </AuthProvider>
            }
          />
          <Route path="/login" element={<Aminiilogin />} />
          <Route
            path="/admin/image-linker"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AuthProvider>
                  <AdminImageLinker />
                </AuthProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/AdminAminii"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AuthProvider>
                  <AdminAminii />
                </AuthProvider>
              </ProtectedRoute>
            }
          />
          {/* All other traffic routes to Maintenance screen */}
          <Route path="*" element={<MaintenanceUnderway />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      {/* Real-time lightweight visitor analytics tracker */}
      <VisitorTracker />

      {/* Slide-out Cart Drawer accessible site-wide */}
      <CartDrawer />

      {/* Floating Admin Button for authenticated Admin users */}
      <AdminFloatingButton />

      <Routes>
        {/* Public routes */}
        <Route path="/" element={<AminiiDashboard />} />
        <Route path="/login" element={<Aminiilogin />} />
        <Route path="/register" element={<Aminiiregister />} />
        <Route path="/forgotPassword" element={<ForgotPasswordPage />} />
        <Route path="/track" element={<AminiiTrackOrder />} />

        {/* Admin Bulk Upload Route */}
        <Route
          path="/admin/bulk-upload"
          element={
            <AuthProvider>
              <AdminBulkUpload />
            </AuthProvider>
          }
        />

        {/* Admin Image Linker Route */}
        <Route
          path="/admin/image-linker"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AuthProvider>
                <AdminImageLinker />
              </AuthProvider>
            </ProtectedRoute>
          }
        />

        {/* Maintenance preview route */}
        <Route path="/maintenance" element={<MaintenanceUnderway />} />

        <Route path="/about" element={<AminiiAbout />} />
        <Route path="/contact-us" element={<AminiiContact />} />
        <Route path="/shipping-policy" element={<AminiiPolicies />} />
        <Route path="/return-policy" element={<AminiiPolicies />} />
        <Route path="/cancellation-policy" element={<AminiiPolicies />} />
        <Route path="/refund-policy" element={<AminiiPolicies />} />
        <Route path="/privacy-policy" element={<AminiiPolicies />} />
        <Route path="/terms-of-service" element={<AminiiPolicies />} />

        {/* Still-public product pages */}
        <Route path="/productList" element={<AminiiProductList />} />
        <Route path="/productList/:category" element={<AminiiProductList />} />
        <Route
          path="/productList/:category/:subcategory"
          element={<AminiiProductList />}
        />
        <Route
          path="/productDetails/:productId"
          element={<AminiiProductDetails />}
        />

        {/* Protected routes */}
        <Route path="/addtocart" element={<AddToCart />} />
        <Route path="/checkout" element={<AminiiCheckout />} />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <AminiiOrderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/AdminAminii"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AuthProvider>
                <AdminAminii />
              </AuthProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={<AminiiErrorPage message="This page doesn't exist." showHomeButton={true} />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
